import React, { useEffect, useState } from 'react'
import { useSearchBox, useRefinementList, useHits, useInstantSearch } from 'react-instantsearch-hooks-web'
import { Hit } from 'instantsearch.js'
import Link from 'components/Link'
import { classNames } from 'lib/utils'
import { useSearch, SearchResultType } from './SearchContext'
import { navigate } from 'gatsby'
import { Combobox, RadioGroup } from '@headlessui/react'
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { StaticImage } from 'gatsby-plugin-image'
import { CallToAction } from 'components/CallToAction'
import { Search } from 'components/Icons/Icons'
import useiCloudReady from '../../hooks/useiCloudReady'

type Result = Hit<{
    id: string
    type: 'blog' | 'docs' | 'api' | 'question' | 'howwework' | 'marketplace'
    title: string
    slug: string
    schema?: {
        httpVerb: string
        path: string
    }[]
    headings: {
        value: string
        depth: number
        fragment: string
    }[]
    excerpt: string
}>

type Category = typeof categories[number]

const categories = [
    {
        type: 'all',
        name: 'All',
    },
    {
        type: 'docs',
        name: 'Docs',
    },
    {
        type: 'marketplace',
        name: 'Marketplace',
    },
    {
        type: 'blog',
        name: 'Blog',
    },
    {
        type: 'tutorial',
        name: 'Tutorials',
    },
    {
        type: 'question',
        name: 'Questions',
    },
    {
        type: 'howwework',
        name: 'HowWeWork',
    },
    {
        type: 'api',
        name: 'API',
    },
]

// Mod function that can handle negative numbers
function mod(n: number, m: number) {
    return ((n % m) + m) % m
}

type SearchResultsProps = {
    initialFilter?: SearchResultType
}

export default function SearchResults(props: SearchResultsProps) {
    const [category, setCategory] = useState<Category>(
        props.initialFilter
            ? (categories.find((category) => category.type === props.initialFilter) as Category)
            : categories[0]
    )
    const { items, refine } = useRefinementList({ attribute: 'type', sortBy: ['name:asc'] })
    const { close } = useSearch()

    const { query } = useSearchBox()
    const icloudready = useiCloudReady()

    const onSelect = (result: Result) => {
        icloudready?.capture('web search result clicked', {
            objectID: result.objectID,
            title: result.title,
            slug: result.slug,
            category: category.type,
            query,
            type: result.type,
        })

        close()
        navigate('/' + result.slug)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab') {
            event.preventDefault()

            setCategory((category) => {
                const typeSet = new Set(items.map((item) => item.value))

                const extendedItems = categories.reduce<typeof categories[number][]>((acc, category) => {
                    if (category.type === 'all' || typeSet.has(category.type)) {
                        return [...acc, category]
                    } else {
                        return acc
                    }
                }, [])

                const currentIdx = extendedItems.findIndex((item) => item.name === category.name)

                if (items.length === 0 || items.length === -1) {
                    return category
                }

                return !event.shiftKey
                    ? extendedItems[mod(currentIdx + 1, extendedItems.length)]
                    : extendedItems[mod(currentIdx - 1, extendedItems.length)]
            })
        }
    }

    const compareResults = (a: Result, b: Result) => {
        return a.objectID === b.objectID
    }

    return (
        <Combobox value={{} as Result} onChange={onSelect} by={compareResults}>
            {({ activeOption }) => (
                <div
                    className="search-results z-50 flex h-full flex-col overflow-hidden rounded bg-white shadow-xl"
                    onKeyDown={handleKeyDown}
                >
                    <SearchBox />
                    <Hits activeOption={activeOption} close={close} />
                    <RefinementList
                        initialFilter={props.initialFilter}
                        category={category}
                        setCategory={setCategory}
                        refine={refine}
                        items={items}
                    />
                </div>
            )}
        </Combobox>
    )
}

const SearchBox = () => {
    const [query, setQuery] = useState('')
    const { close } = useSearch()
    const { refine } = useSearchBox()

    useEffect(() => {
        refine(query)
    }, [query])

    return (
        <div className="relative flex items-center border-b border-gray-accent-light/50 dark:border-b-2 dark:border-black">
            <div className="absolute left-4 z-20">
                <Search className="h-5 w-5 opacity-40" />
            </div>
            <Combobox.Input
                className="w-full border-none bg-white py-3 pl-11 pr-4 font-medium ring-0 placeholder:text-black/25 focus:outline-none focus:ring-0 dark:bg-gray-accent-dark dark:placeholder:text-white/25"
                placeholder={`Search ${process.env.GATSBY_BASE_DOMAIN}...`}
                autoComplete="off"
                onKeyDown={(event: React.KeyboardEvent) => (event.key === 'Tab' ? event.preventDefault() : null)}
                value={query}
                displayValue={() => query}
                onChange={(event) => {
                    setQuery(event.target.value)
                }}
            />

            <kbd
                role="button"
                className="absolute right-4 hidden cursor-pointer rounded-sm border border-b-2 border-gray-accent-light/50 px-1.5 py-0.5 font-sans text-xs  dark:border-gray-accent-dark/50 dark:text-white/40 md:block"
                onClick={close}
                style={{ fontSize: '10px' }}
            >
                ESC
            </kbd>
        </div>
    )
}

type RefinementListProps = {
    initialFilter?: SearchResultType
    category: Category
    setCategory: React.Dispatch<React.SetStateAction<Category>>
    items: RefinementListItem[]
    refine: (value: string) => void
}

const RefinementList: React.FC<RefinementListProps> = (props) => {
    const [selected, setSelected] = React.useState<Category>(props.category)
    const icloudready = useiCloudReady()

    useEffect(() => {
        if (props.initialFilter) {
            props.refine(props.initialFilter)
        }
    }, [props.initialFilter])

    useEffect(() => {
        if (selected.type !== 'all') {
            props.refine(selected.type)
        }

        if (props.category.type !== 'all') {
            props.refine(props.category.type)
        }

        icloudready?.capture('web search category refine', {
            previous: selected.type,
            category: props.category.type,
        })

        setSelected(props.category)
    }, [props.category])

    return (
        <RadioGroup
            value={props.category}
            onChange={props.setCategory}
            className="-mt-[1px] bg-tan/25 dark:bg-gray-accent-dark"
        >
            <RadioGroup.Label className="sr-only">Filter results by category</RadioGroup.Label>
            <div className="flex list-none items-center overflow-auto p-0 dark:border-t dark:border-black md:flex-wrap">
                {categories.map((item) => {
                    const result = props.items.find((res) => res.value === item.type)

                    return (
                        <RadioGroup.Option
                            key={item.type}
                            value={item}
                            disabled={!result?.count && item.type !== 'all'}
                            onClick={() => {
                                if (item.type === props.category.type) {
                                    props.setCategory(categories[0])
                                }
                            }}
                            className={
                                'relative cursor-pointer px-3 py-2 before:absolute before:top-[-1px] before:left-0 before:right-0 before:h-[2px] first:px-5 ui-checked:before:h-[3px] ui-checked:before:bg-red ui-not-checked:text-gray-accent-dark ui-not-checked:before:bg-transparent ui-not-checked:hover:before:bg-black/40 dark:ui-not-checked:text-gray-accent-light'
                            }
                        >
                            {({ checked }) => (
                                <label
                                    className={classNames(
                                        'flex select-none items-baseline space-x-1',
                                        result?.count !== 0 && 'cursor-pointer'
                                    )}
                                >
                                    <span className="text-sm ui-checked:font-bold">{item.name}</span>
                                    {item.type !== 'all' && (
                                        <span
                                            className={classNames(
                                                'text-xs',
                                                checked ? ' dark:text-white/40' : ' dark:text-white/40'
                                            )}
                                        >
                                            {result?.count || 0}
                                        </span>
                                    )}
                                </label>
                            )}
                        </RadioGroup.Option>
                    )
                })}
            </div>
        </RadioGroup>
    )
}

type HitsProps = {
    activeOption: Result | null
    close: () => void
}

const Hits: React.FC<HitsProps> = ({ activeOption, close }) => {
    const [initialLoad, setInitialLoad] = React.useState(false)
    const { hits } = useHits<Result>()
    const { status } = useInstantSearch()
    const icloudready = useiCloudReady()
    const { query } = useSearchBox()

    useEffect(() => {
        if (!initialLoad && hits.length > 0) {
            setInitialLoad(true)
        }
    }, [initialLoad, hits])

    const onSelectHeading = (heading: { value: string; depth: number; fragment: string }) => {
        icloudready?.capture('web search result heading clicked', {
            objectID: activeOption?.objectID,
            title: activeOption?.title,
            slug: activeOption?.slug,
            fragment: heading.fragment,
            query,
            heading: heading.value,
            type: activeOption?.type,
        })
        close()
    }

    return (
        <div className="grid min-h-0 flex-grow border-b border-gray-accent-light dark:border-gray-accent-dark md:grid-cols-2">
            <section className="overflow-y-auto overscroll-none border-r border-gray-accent-light/50 bg-white text-left dark:border-gray-accent-dark/50 dark:bg-gray-accent-dark">
                {!initialLoad || status === 'stalled' ? (
                    <ol className="m-0 list-none p-0 dark:bg-black">
                        {new Array(5).fill({}).map((_, index) => (
                            <li
                                key={index}
                                className="-mt-px space-y-1 border-y border-gray-accent-light/20 px-2 py-3 dark:border-gray-accent-dark/80"
                            >
                                <div className="h-3.5 w-24 animate-pulse rounded-sm bg-gray-accent-light/60 dark:bg-gray-accent-dark/80"></div>
                                <div className="h-5 w-64 animate-pulse rounded-sm bg-gray-accent-light/60 dark:bg-gray-accent-dark/80"></div>
                                <div className="h-3 w-32 animate-pulse rounded-sm bg-gray-accent-light/60 dark:bg-gray-accent-dark/80"></div>
                            </li>
                        ))}
                    </ol>
                ) : hits.length > 0 ? (
                    <Combobox.Options as="ol" className="m-0 list-none p-0 dark:bg-black" static hold>
                        {hits.map((hit) => {
                            return (
                                <Combobox.Option
                                    key={hit.objectID}
                                    value={hit}
                                    className="group -mt-px border-b border-t border-gray-accent-light/25 pl-3 pr-2 last:border-b-0 hover:border-gray-accent-light/30 hover:bg-tan/50 ui-active:border-gray-accent-light/30 ui-active:bg-tan/50 dark:border-gray-accent-dark/80 dark:hover:border-gray-accent-dark/90 dark:hover:bg-gray-accent-dark/50 dark:ui-active:border-gray-accent-dark/90 dark:ui-active:bg-gray-accent-dark"
                                >
                                    <Link
                                        className="flex w-full flex-col space-y-0.5 px-2 py-3 font-semibold leading-tight text-black/75 focus:outline-none group-hover:text-black/100 dark:text-white/75 dark:group-hover:text-white/100"
                                        to={'/' + hit.slug}
                                        onClick={() => close()}
                                    >
                                        <span
                                            className={`text-[13px] font-normal ${
                                                hit.type === 'api' ? 'uppercase' : 'capitalize'
                                            }  rounded-full dark:text-white/60`}
                                        >
                                            {hit.type}
                                        </span>

                                        <span className="font-semibold line-clamp-1">{hit.title}</span>
                                        {/* <p className="text-sm font-normal m-0 text-gray line-clamp-2">{hit.excerpt}</p> */}
                                        <span className="text-[13px] font-normal">
                                            <span className=" opacity-[35%] dark:text-white">
                                                {process.env.GATSBY_BASE_DOMAIN}/
                                            </span>
                                            <span className=" dark:text-white/50">{hit.slug}</span>
                                        </span>
                                    </Link>
                                </Combobox.Option>
                            )
                        })}
                    </Combobox.Options>
                ) : (
                    <>
                        <div className="p-8">
                            <div className="text-center">
                                <h3 className="mb-0 text-xl">No results</h3>
                                <p className="mb-0 text-[15px] opacity-75">
                                    This doesn't happen often, but we're stumped!
                                </p>
                            </div>

                            <div className="mb-4 text-center">
                                <StaticImage
                                    placeholder="none"
                                    loading="eager"
                                    quality={100}
                                    objectFit="contain"
                                    alt=""
                                    src="../../images/detective_ready.png"
                                    className="max-w-[150px]"
                                />
                            </div>

                            <div className="rounded border border-gray-accent-light bg-tan/50 p-4 dark:border-gray-accent-dark dark:bg-almost-black/100">
                                <h5 className="mb-0 text-base opacity-75">Tip: Ask the support</h5>
                                <p className="mb-4 text-sm opacity-80">
                                    Our team monitor the Questions page. Somone's bound to know the answer!
                                </p>

                                <CallToAction type="primary" size="sm" width="full" className="" href="/questions">
                                    Ask a question
                                </CallToAction>
                            </div>
                        </div>
                    </>
                )}
            </section>
            <section className="hidden h-full overflow-y-auto bg-tan/50 p-2 dark:bg-almost-black md:block">
                {activeOption ? (
                    <div className="rounded border border-gray-accent-light/40 bg-white p-6 dark:border-gray-accent-dark dark:bg-gray-accent-dark">
                        <div className="text-left">
                            <span
                                className={`block text-sm font-semibold  dark:text-white/50 ${
                                    activeOption.type === 'api' ? 'uppercase' : 'capitalize'
                                } mb-1`}
                            >
                                {activeOption.type}
                            </span>
                            <h4 className="mb-3 text-2xl leading-[1.125]">{activeOption.title}</h4>
                            <p className=" mb-0 text-[15px] dark:text-white/80">{activeOption.excerpt}</p>
                            {activeOption.type !== 'question' && activeOption.type !== 'marketplace' ? (
                                <span className="mt-5 mb-3  block text-xs font-semibold dark:text-white/60">
                                    On this page
                                </span>
                            ) : (
                                <></>
                            )}
                            {activeOption.type === 'api' ? (
                                <ol className="m-0 list-none space-y-2  text-sm font-semibold dark:text-white/60">
                                    {activeOption?.schema?.map((endpoint, index) => {
                                        return (
                                            <li
                                                key={activeOption.type + endpoint.httpVerb + endpoint.path + index}
                                                className="flex items-center space-x-2 whitespace-nowrap font-code text-xs"
                                            >
                                                <span className="uppercase">{endpoint.httpVerb}</span>
                                                <div className="flex-shrink overflow-hidden text-ellipsis">
                                                    <span>{endpoint.path}</span>
                                                </div>
                                            </li>
                                        )
                                    })}
                                </ol>
                            ) : (
                                <ol className="m-0 list-none space-y-2 p-0 font-semibold">
                                    {activeOption?.headings
                                        ?.filter(({ depth }) => depth <= 2)
                                        .map((heading, index) => {
                                            return (
                                                <li
                                                    key={activeOption.type + heading.value + index}
                                                    className="jumpTo group pl-6 text-sm"
                                                >
                                                    <Link
                                                        to={`/${activeOption.slug}#${heading.fragment}`}
                                                        onClick={() => onSelectHeading(heading)}
                                                        className=" group-hover:text-black/90 dark:text-white/60 dark:group-hover:text-white/90"
                                                    >
                                                        {heading.value}
                                                    </Link>
                                                </li>
                                            )
                                        })}
                                </ol>
                            )}
                        </div>
                    </div>
                ) : null}
            </section>
        </div>
    )
}
